import { useCallback, useEffect, useRef, useState } from 'react';

import { CLICK_IDS } from '@sunflower-games/analytics-events';

import type { CashOffer } from '../../../dto/wallet.types';
import { getConnectionDiagnostics, getLoadTimeoutMs } from '../../../hooks/iframe-helpers/connectionDiagnostics';
import { useCoinsAnimation } from '../../../hooks/useCoinsAnimation';
import { useGoHome } from '../../../hooks/useGoHome';
import { useOpenOrder, type OpenOrderData } from '../../../hooks/useOpenOrder';
import { useOpenOrderWithKYC } from '../../../hooks/useOpenOrderWithKYC';
import { useSideMenu } from '../../../hooks/useSideMenu';
import { useToggleCoin } from '../../../hooks/useToggleCoin';
import { logToNewRelic } from '../../../services/debug';
import { intercom } from '../../../services/intercom';
import { useFlag } from '../../../services/launchDarkly';
import type { CoinTypeStore } from '../../../stores/cointype/CoinTypeStore';
import type { GameStore } from '../../../stores/game/GameStore';
import type { ClaimableReward } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';
import { devLog } from '../../../utils/log';

const MAX_RETRIES = 2;

const isValidAmount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

// Game-ui relays the server-computed `cashOffer` from its own wallet fetch alongside the
// displayed cash. Accept only a well-formed offer; anything else seeds null so a stale
// offer never outlives the balance it was computed for.
const parseCashOffer = (value: unknown): CashOffer | null => {
  const offer = value as CashOffer | null | undefined;
  if (
    offer &&
    isValidAmount(offer.price) &&
    isValidAmount(offer.scAmount) &&
    isValidAmount(offer.gcAmount) &&
    isValidAmount(offer.gcPerDollar)
  ) {
    return { price: offer.price, scAmount: offer.scAmount, gcAmount: offer.gcAmount, gcPerDollar: offer.gcPerDollar };
  }
  return null;
};

// Which game (and provider) a load/timeout belongs to. `gameId` is the route param, set on Game
// page mount, so it is present even when the iframe never gets far enough to launch anything.
// `currentGame` is only populated once launchGame resolves — which is exactly what does NOT happen
// on a 'no gameUiReady message' timeout, so `currentGameId` on its own was null on ~85% of these
// events and left "which game failed to load" unanswerable from logs. Both are kept: a present
// `gameId` next to a missing `currentGameId` is itself the signal that the failure preceded launch.
// Module-level (like getConnectionDiagnostics) so it stays out of the hooks' dependency arrays.
const getGameContext = (gameStore: GameStore, coinTypeStore: CoinTypeStore) => ({
  gameId: gameStore.gameId,
  currentGameId: gameStore.currentGame?.gameId,
  gameName: gameStore.currentGame?.gameName,
  gameIdentifier: gameStore.currentGame?.identifier,
  providerName: gameStore.currentGame?.providerName,
  providerId: gameStore.currentGame?.providerId,
  coinType: coinTypeStore.coinType,
});

export const useGameIframeEvents = ({ iframeRef }: { iframeRef: React.RefObject<HTMLIFrameElement> }) => {
  const { startAnimation } = useCoinsAnimation();
  const {
    appStore,
    coinTypeStore,
    gameStore,
    payRedeemStore,
    prizeDropStore,
    uiStore,
    minigamesStore,
    lowBalanceStore,
    walletStore,
    crownRipsStore,
    eventTriggerStore,
  } = useStores();
  const { handleGoHome } = useGoHome();
  const { toggleCoin } = useToggleCoin();
  const { handleToggle } = useSideMenu();
  const { openOrder } = useOpenOrder();
  const inGameStoreEnabled = useFlag('game_ui_mini_store_enabled', false);

  const handleOpenStoreOrder = useCallback(
    (storePackage: OpenOrderData) => {
      openOrder({ data: storePackage, isMiniStore: true, isStoreItem: true, purchaseSource: 'mini_store' });
    },
    [openOrder],
  );
  const openStoreOrderWithKyc = useOpenOrderWithKYC(handleOpenStoreOrder);

  const { prizeDropEventByCoin } = prizeDropStore;

  const iframeElRef = useRef<HTMLIFrameElement | null>(null);
  const retryCountRef = useRef(0);
  const loadStartTimeRef = useRef<number>(0);
  const receivedReadyMessageRef = useRef(false);

  const [isIframeLoading, setIsIframeLoading] = useState(!!gameStore.gameUiUrl);
  // Game-ui starts rendering its own loader the moment it posts the 'slots' ready message,
  // but `slotsGameUiIframe` is only stored 1s later (handleSlotsGameIframeLoad delay). Track
  // readiness separately so the host loader can drop as soon as game-ui is visible.
  const [isSlotsUiReady, setIsSlotsUiReady] = useState(false);

  const getElapsedMs = () =>
    loadStartTimeRef.current ? Math.round(performance.now() - loadStartTimeRef.current) : null;

  const onGameIframeError = () => {
    console.error(`Error loading Game UI iframe | ${JSON.stringify({ elapsedMs: getElapsedMs() })}`);
    gameStore.setIsGameUiError(true);
    setIsIframeLoading(false);
  };

  const retryIframeLoad = useCallback(() => {
    console.log(`Game UI iframe manual retry by user | ${JSON.stringify(getConnectionDiagnostics())}`);
    retryCountRef.current = 0;
    loadStartTimeRef.current = performance.now();
    receivedReadyMessageRef.current = false;
    gameStore.setSlotsGameUiIframe(null);
    gameStore.setIsGameUiError(false);
    setIsSlotsUiReady(false);
    setIsIframeLoading(!!gameStore.gameUiUrl);
  }, [gameStore]);

  const onIframeLoad = useCallback(() => {
    const elapsedMs = getElapsedMs();
    console.log(`Game UI iframe loaded | ${JSON.stringify({ elapsedMs })}`);
    void logToNewRelic('Game UI iframe DOM loaded', { elapsedMs, ...getGameContext(gameStore, coinTypeStore) }, 'info');
    setIsIframeLoading(false);
  }, [gameStore, coinTypeStore]);

  const onGameUiMessage = useCallback(
    (event: MessageEvent) => {
      const { data, origin } = event;

      if (origin !== import.meta.env.VITE_GAME_UI_URL || data.source !== 'game') {
        return;
      }

      if (gameStore.isGameUiError) {
        devLog(
          `useGameIframeEvents Ignoring message from game UI because it is in error state: ${JSON.stringify(data)}`,
        );
        return;
      }

      devLog(`useGameIframeEvents Received message from game UI: ${JSON.stringify(data)}`);

      switch (data.type) {
        case 'gameUiReadyForMessages': {
          const payloadType = data.payload?.type;
          const elapsedMs = getElapsedMs();
          console.log(`Game UI iframe gameUiReadyForMessages | ${JSON.stringify({ elapsedMs, payloadType })}`);
          void logToNewRelic(
            'Game UI ready for messages',
            { payloadType, elapsedMs, ...getGameContext(gameStore, coinTypeStore) },
            'info',
          );

          receivedReadyMessageRef.current = true;

          if (payloadType === 'auth') {
            void gameStore.sendCustomTokenToSlotsGameUi();
            return;
          }

          if (payloadType !== 'slots') {
            return;
          }

          setIsSlotsUiReady(true);

          setTimeout(() => {
            void gameStore.handleSlotsGameIframeLoad(iframeElRef.current!);
          }, 1000);
          break;
        }
        case 'openSupport': {
          intercom.handleOpenSupport();
          break;
        }
        case 'goHome': {
          handleGoHome();
          break;
        }
        case 'headerCoinToggleClick': {
          toggleCoin();
          break;
        }
        case 'headerMenuClick': {
          handleToggle();
          break;
        }
        case 'escapeKeyClick': {
          if (uiStore.isVisibleMiniStore) {
            payRedeemStore.closeMiniStore();
          }
          break;
        }
        case 'relaunchGame': {
          if (gameStore.currentGame?.gameId) {
            void gameStore.launchGame(gameStore.currentGame.gameId, coinTypeStore.coinType, handleGoHome);
          } else {
            console.warn(`useGameIframeEvents relaunchGame message missing gameId, data: ${JSON.stringify(data)}`);
          }
          break;
        }
        case 'miniStoreClick': {
          if (inGameStoreEnabled) {
            break;
          }
          // Game-ui owns the live cash balance during play; seed the host wallet with the value it is
          // currently showing (plus the server-computed offer it relays) so the dynamic cash offer opens
          // in step with what the player sees (the host wallet can otherwise be stale — the external-slot
          // cash poll doesn't run for game-ui games).
          const displayedCash = data.payload?.cash;
          if (isValidAmount(displayedCash)) {
            walletStore.setWallet({ cash: displayedCash, cashOffer: parseCashOffer(data.payload?.cashOffer) });
          }
          payRedeemStore.toggleMiniStore({
            eventAction: 'click',
            triggeredBy: 'click',
            buttonLocation: 'header',
            clickId: CLICK_IDS.PURCHASE.HEADER_BALANCE_WRAPPER,
          });
          break;
        }
        case 'coinsAnimation': {
          // Same purchase coin animation the mini store QUICK BUY plays — game-ui delegates
          // here (e.g. the low-balance cash offer popup) so both purchases look identical.
          const rewardsPayload: { coinType?: string; coinValue?: number }[] = Array.isArray(data.payload)
            ? data.payload
            : [];
          const scAmount = rewardsPayload.find((r) => r.coinType === 'SC')?.coinValue || 0;
          const gcAmount = rewardsPayload.find((r) => r.coinType === 'GC')?.coinValue || 0;

          const rewards: ClaimableReward[] = [{ scCoin: Number(scAmount), gcCoin: Number(gcAmount), bundleItems: [] }];
          startAnimation({ rewards });
          break;
        }
        case 'lowBalanceOpenMiniStore': {
          if (inGameStoreEnabled) {
            break;
          }
          // Same wallet seeding as miniStoreClick: game-ui owns the live cash during play, and
          // the mini store's dynamic cash offer must reflect it (the host wallet can be stale).
          const lowBalanceCash = data.payload?.cash;
          if (isValidAmount(lowBalanceCash)) {
            walletStore.setWallet({ cash: lowBalanceCash, cashOffer: parseCashOffer(data.payload?.cashOffer) });
          }
          lowBalanceStore.openMiniStoreOnLowBalance(data.payload?.trigger || 'low_balance');
          break;
        }
        case 'openStoreOrder': {
          const storePackage = data.payload;
          // The registry is keyed by numeric packageId; postMessage may deliver it as a string,
          // so normalize before the lookup to avoid always missing and falling back to raw payload.
          const packageId = Number(storePackage?.packageId);
          if (!Number.isFinite(packageId) || packageId <= 0) {
            console.warn(`useGameIframeEvents openStoreOrder invalid packageId, data: ${JSON.stringify(data)}`);
            break;
          }
          const fullPackage = appStore.storePackagesRegistry.get(packageId);
          if (fullPackage) {
            openStoreOrderWithKyc({ imageUrl: '', ...fullPackage });
            break;
          }
          console.warn(
            `useGameIframeEvents openStoreOrder package not in registry and payload incomplete, data: ${JSON.stringify(data)}`,
          );
          break;
        }
        case 'prizeDropTimerEnd': {
          prizeDropStore.resetEvent();
          break;
        }
        case 'headerPrizeDropWidgetClick': {
          if (!prizeDropEventByCoin) {
            return;
          }

          void prizeDropStore.startPrizeDropGame({ type: 'info' });

          break;
        }
        case 'startMinigame': {
          if (!data.payload?.gameId) {
            console.warn(`useGameIframeEvents startMinigame message missing gameId, data: ${JSON.stringify(data)}`);
            return;
          }

          uiStore.setCustomLandingCoinAnimation({
            gcLandingPoint: data.payload.gcLandingPoint,
            scLandingPoint: data.payload.scLandingPoint,
          });

          void minigamesStore.startMinigame(data.payload.gameId);
          break;
        }
        case 'ripsPackClaimed': {
          // A rips pack was granted from an in-slot notification claim during regular slot play —
          // the widget's pending-pack badge lives outside game-ui and can't observe that on its own.
          void crownRipsStore.fetchPendingPacksCount();
          break;
        }
        case 'prizeDropExtraOfferClaimed': {
          const benefitKey = (data.payload as { benefitKey?: string | null } | undefined)?.benefitKey;
          if (benefitKey) void eventTriggerStore.openGrantedOfferByBenefitKey(benefitKey);
          break;
        }
        default: {
          console.warn(`useGameIframeEvents Unhandled message from game UI: ${JSON.stringify(data)}`);
        }
      }
    },
    [
      appStore,
      gameStore,
      coinTypeStore,
      handleGoHome,
      toggleCoin,
      handleToggle,
      uiStore,
      payRedeemStore,
      prizeDropStore,
      prizeDropEventByCoin,
      minigamesStore,
      lowBalanceStore,
      walletStore,
      startAnimation,
      openStoreOrderWithKyc,
      inGameStoreEnabled,
      crownRipsStore,
      eventTriggerStore,
    ],
  );

  useEffect(() => {
    devLog('useGameIframeEvents GameIframe listening for gameUiReady message');
    window.addEventListener('message', onGameUiMessage);

    return () => {
      devLog('useGameIframeEvents GameIframe removing gameUiReady message listener');
      window.removeEventListener('message', onGameUiMessage);
    };
  }, [onGameUiMessage]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    iframeElRef.current = iframe;
    if (!loadStartTimeRef.current) {
      loadStartTimeRef.current = performance.now();
    }
    iframe.addEventListener('load', onIframeLoad);

    return () => {
      iframe.removeEventListener('load', onIframeLoad);
    };
  }, [iframeRef, onIframeLoad]);

  useEffect(() => {
    if (!gameStore.gameUiUrl || gameStore.isGameUiError || (!isIframeLoading && gameStore.slotsGameUiIframe)) {
      return;
    }

    const timeoutMs = getLoadTimeoutMs();

    const timeout = setTimeout(() => {
      if (!isIframeLoading && gameStore.slotsGameUiIframe) {
        return;
      }

      const iframeAlive = receivedReadyMessageRef.current;
      const diagnostics = { ...getConnectionDiagnostics(), elapsedMs: getElapsedMs(), timeoutMs, iframeAlive };
      const reason = isIframeLoading && !iframeAlive ? 'load timeout' : 'no gameUiReady message (slots)';

      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        const attempt = retryCountRef.current;

        console.warn(`Game UI iframe ${reason}, retry ${attempt}/${MAX_RETRIES} | ${JSON.stringify(diagnostics)}`);
        void logToNewRelic(
          'Game UI iframe retry',
          {
            reason,
            retryAttempt: attempt,
            maxRetries: MAX_RETRIES,
            ...getGameContext(gameStore, coinTypeStore),
            diagnostics,
          },
          'warn',
        );

        const iframe = iframeElRef.current;
        if (iframe) {
          const currentSrc = iframe.src;
          iframe.src = '';
          iframe.src = currentSrc;
          setIsSlotsUiReady(false);
          setIsIframeLoading(true);
        }
      } else {
        console.error(`Game UI iframe ${reason} after all retries | ${JSON.stringify(diagnostics)}`);
        void logToNewRelic(
          'Game UI iframe final error after retries',
          {
            reason,
            maxRetries: MAX_RETRIES,
            ...getGameContext(gameStore, coinTypeStore),
            diagnostics,
          },
          'error',
        );

        gameStore.setIsGameUiError(true);
        setIsIframeLoading(false);
      }
    }, timeoutMs);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    gameStore,
    coinTypeStore,
    isIframeLoading,
    gameStore.gameUiUrl,
    gameStore.slotsGameUiIframe,
    gameStore.isGameUiError,
  ]);

  return {
    isIframeLoading,
    isSlotsUiReady,
    onGameIframeError,
    retryIframeLoad,
  };
};
